import moment from 'moment';
import React from 'react';
import Message from './message';
import { useGroupChatContext } from '../../context/groupchatContext';
import { isLoggedInUser } from '../../helpers/utility';

const MessageList = React.memo(({ messages, isSkipTimeBanner }) => {

    const msgDates = new Set();
    const groupChatContext = useGroupChatContext();
    const { authData, selectedChat, selectedChatTab } = groupChatContext;
    const isChronologicalOrder = messages?.length > 1 &&
        moment(messages[0]?.created).isSameOrBefore(moment(messages[messages.length - 1]?.created));

    const isUnreadMessage = (message) => {
        if (!message?.created || isLoggedInUser(authData, message)) {
            return false;
        }
        const lastRead = selectedChat?._openedLastRead || selectedChat?.last_read;
        if (lastRead) {
            return moment(message.created).isAfter(moment(lastRead));
        }
        return false;
    }

    const getCountBoundaryIndex = (unreadCount) => {
        if (!unreadCount || !messages?.length) {
            return -1;
        }
        const messageEntries = isChronologicalOrder
            ? [...messages.entries()].reverse()
            : [...messages.entries()];
        const unreadIncomingIndexes = [];

        for (const [messageIndex, message] of messageEntries) {
            if (!isLoggedInUser(authData, message)) {
                unreadIncomingIndexes.push(messageIndex);
            }
            if (unreadIncomingIndexes.length >= unreadCount) {
                break;
            }
        }

        if (unreadIncomingIndexes.length < unreadCount) {
            return -1;
        }

        if (isChronologicalOrder) {
            return Math.min(...unreadIncomingIndexes) - 1;
        }

        return Math.max(...unreadIncomingIndexes);
    }

    const getLastReadBoundaryIndex = () => {
        if (!(selectedChat?._openedLastRead || selectedChat?.last_read)) {
            return -1;
        }

        return messages?.findIndex((message, index) => {
            const nextMessage = messages[index + 1];
            return isChronologicalOrder
                ? !isUnreadMessage(message) && isUnreadMessage(nextMessage)
                : isUnreadMessage(message) && !isUnreadMessage(nextMessage);
        });
    }

    const getUnreadLineIndex = () => {
        const unreadCount = Number(selectedChat?._openedUnreadMessageCount || 0);
        const countBoundaryIndex = getCountBoundaryIndex(unreadCount);

        if (selectedChatTab === 'location_chats') {
            return countBoundaryIndex;
        }

        const lastReadBoundaryIndex = getLastReadBoundaryIndex();
        if (lastReadBoundaryIndex > -1) {
            return lastReadBoundaryIndex;
        }

        return countBoundaryIndex;
    }

    const unreadLineIndex = getUnreadLineIndex();

    const renderUnreadLine = () => (
        <div className="flex items-center gap-3 py-2 text-xs font-medium text-blue-500">
            <div className="h-px flex-1 bg-blue-400" />
            <span>Unread</span>
            <div className="h-px flex-1 bg-blue-400" />
        </div>
    )

    const formatMsgDate = (created_date) => {

        const today = moment().startOf('day');
        const msgDate = moment(created_date);
        let dateDay = '';

        if(msgDate.isSame(today, 'day')){
            dateDay = 'Today';
        }
        else if (msgDate.isSame(today.clone().subtract(1, 'days'), 'day')){
            dateDay = 'Yesterday';
        }
        else{
            dateDay = msgDate.format('MMMM D, YYYY');
        }

        return dateDay
    }

    const renderMsgDate = (message, nextMessage) => {

        if(!message?.created){
            return null;
        }

        const dateTimeStamp = moment(message.created, 'YYYY-MM-DD').valueOf();
        let nextMsgdateTimeStamp = '';
        if(nextMessage){
            nextMsgdateTimeStamp = moment(nextMessage.created, 'YYYY-MM-DD').valueOf();
        }
        
        if(msgDates.has(dateTimeStamp) || (nextMsgdateTimeStamp && nextMsgdateTimeStamp == dateTimeStamp)){
            return null
        }
        else{
            msgDates.add(dateTimeStamp);
            return (
                <div className='py-2 flex justify-center items-center'>
                    <span id="msg_day" className='px-2 py-1 shadow font-medium rounded-lg text-gray-400 bg-white border-gray-300'>
                        {formatMsgDate(message.created)}
                    </span>
                </div>
            )
        }
    }

    return (
        <>
            {messages?.map((message, index) => (
                <React.Fragment key={(message?.id || message?.temp_message_id || "message") + " - " + index}>
                    <Message key={(message?.id || "") + " - " + index} msg={message} />
                    {index === unreadLineIndex ? renderUnreadLine() : null}
                    {isSkipTimeBanner ? null : renderMsgDate(message, messages[index+1])}
                </React.Fragment>
            ))}
        </>
    );
});

export default MessageList;